import { useState, useEffect, useRef } from 'react';
import usePerformanceOptimization from './usePerformanceOptimization';

export const useLazyAnimation = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '50px',
    triggerOnce = true,
    delay = 0
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const elementRef = useRef(null);
  const { shouldReduceAnimations } = usePerformanceOptimization();

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // If animations are reduced, load immediately
    if (shouldReduceAnimations) {
      setShouldLoad(true);
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Add delay before loading heavy animations
          const timer = setTimeout(() => {
            setShouldLoad(true);
          }, delay);

          if (triggerOnce) {
            observer.unobserve(element);
          }

          return () => clearTimeout(timer);
        } else if (!triggerOnce) {
          setIsVisible(false);
          setShouldLoad(false);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce, delay, shouldReduceAnimations]);

  return {
    elementRef,
    isVisible,
    shouldLoad,
    shouldReduceAnimations
  };
};

export default useLazyAnimation;
